/**
 * The count room v4 screens share these: the season the doors are scoped to, the day the room
 * is working, the row shapes the API sends, and the bridge on the station laptop (CR-12).
 */
import { useEffect, useState } from 'react';
import { can } from '@cider/domain/access';
import { personOf, type Me } from '../../shell/session.js';
import { fiscalYearToday, useLoad } from './shared.js';
import { FestivalClock } from '@cider/domain/engines';

/** The season (2026) the count-room doors are scoped to; rows carry the fiscal year (2027). */
export const season = (): number => fiscalYearToday() - 1;

export interface Bills {
  bills100: number;
  bills50: number;
  bills20: number;
  bills10: number;
  bills5: number;
  bills1: number;
}
export const NOTES = ['bills100', 'bills50', 'bills20', 'bills10', 'bills5', 'bills1'] as const;
/** The 2026 grid's labels, unchanged. */
export const LABEL: Record<(typeof NOTES)[number], string> = {
  bills100: '$100s',
  bills50: '$50s',
  bills20: '$20s',
  bills10: '$10s',
  bills5: '$5s',
  bills1: '$1s',
};
export const ZERO_BILLS: Bills = {
  bills100: 0,
  bills50: 0,
  bills20: 0,
  bills10: 0,
  bills5: 0,
  bills1: 0,
};

export interface Register {
  id: string;
  name: string;
  areaId: string | null;
  accountId: string | null;
  tag: {
    id: string;
    tagCode: string;
    areaId: string | null;
    readerDeviceId: string | null;
    accountId: string | null;
    routeOrder: number;
  } | null;
  profile: {
    id: string;
    seedCents: number;
    mix: { bills1: number; bills5: number; bills10: number; bills20: number };
    displayOrder: number;
  } | null;
}
export interface Bag {
  id: string;
  bagCode: string;
  bagType: 'field' | 'seed';
  status: string;
  custodianId: string | null;
  custodianName: string | null;
  currentRegisterId: string | null;
  currentPurpose: string | null;
  contentsCents: number | null;
  currentCountSheetId: string | null;
  retiredReason: string | null;
  updatedAt: string;
}
export interface Sheet {
  id: string;
  sourceId: string;
  festivalDay: string;
  dayName: string;
  mode: 'machine' | 'sheet';
  purpose: 'pickup' | 'close';
  pickupNo: number | null;
  status: 'open' | 'verified' | 'locked' | 'reversed';
  verifiedByMethod: string | null;
  acceptedBy: string | null;
  lockedAt: string | null;
  denominations: Bills & { coinCents: number };
  checkCents: number;
  grossCents: number;
  seedReturnedCents: number;
  netCents: number;
  counters: { id?: string; name: string }[];
  bankBagNumber: string | null;
  notes: string | null;
  cashBagId: string | null;
  isFirstPickupOfDay: boolean;
  state: string;
  createdAt: string;
  createdBy: string | null;
  sheetAttachmentId: string | null;
  reversedReason: string | null;
}
export interface Pass {
  id: string;
  passNo: number;
  bills: Bills;
  totalCents: number;
  rejectCount: number;
  source: string;
  at: string;
}
export interface Settings {
  id: string;
  defaultMode: 'machine' | 'sheet';
  bridgeTimeoutSeconds: number;
  varianceRecountCents: number;
  randomAuditBps: number;
  stations: { id: string; name: string }[];
  defaultModeSetBy: string | null;
  defaultModeSetAt: string | null;
}
export interface DepositRow {
  id: string;
  status: string | null;
  festivalDay: string | null;
  expected: (Bills & { coinCents: number; checkCents: number; totalCents: number }) | null;
  physical: (Bills & { coinCents: number; checkCents: number; totalCents: number }) | null;
  verifyMethod: string | null;
  verifiedBy: string | null;
  bankBagSerial: string | null;
  sealedBy: string | null;
  sealedAt: string | null;
  /** "Sealed by the counter — no second person available" (TH-08), and when. */
  oneHandSays: string | null;
  oneHandAt: string | null;
  releasedTo: string | null;
  releasedAt: string | null;
  bankReceiptCents: number | null;
  depositedAt: string | null;
  memo: string;
  cents: number;
  counts: string[];
  createdAt: string;
}

export function crDoors(me: Me): { room: boolean; runner: boolean; director: boolean } {
  const p = personOf(me);
  const y = season();
  return {
    room: can(p, 'finance.countroom', { write: true, festivalYear: y }).allowed,
    runner: can(p, 'finance.pickup', { write: true, festivalYear: y }).allowed,
    director: can(p, 'finance.deposit', { write: true }).allowed,
  };
}

/** Which day the room is on, as the API answers it (ADR 0023). */
export interface OpenDay {
  day: string;
  /** record: the first festival day not yet sealed · clock: before, after, or no dates. */
  from: 'record' | 'clock';
  days: { day: string; name: string; sealed: boolean }[];
  /** Every day sealed this season — a chosen one is let go. */
  sealed: string[];
}
export interface RoomDayState {
  open: OpenDay | null;
  /** Somebody at this desk chose a day other than the open one. */
  chosen: boolean;
}

const CHOSEN = 'cr.day';
const remembered = (): string | null => {
  try {
    return localStorage.getItem(CHOSEN);
  } catch {
    return null;
  }
};
const remember = (d: string | null): void => {
  try {
    if (d === null) localStorage.removeItem(CHOSEN);
    else localStorage.setItem(CHOSEN, d);
  } catch {
    /* a private window keeps nothing */
  }
};

/**
 * **The room's day** (ADR 0023): the API's open day — the first festival day not yet sealed —
 * unless somebody at this desk chose another. The browser keeps only that choice, never a guess,
 * and lets it go once its day is sealed, so a laptop left on Friday opens on Saturday. Until the
 * API answers (a moment), a desk with no choice reads the operating day.
 */
export function useDay(): [string, (d: string) => void, RoomDayState] {
  const [chosen, setChosen] = useState<string | null>(remembered);
  const load = useLoad<OpenDay>(`/money/countroom/${String(season())}/open-day`);
  const open = load.data ?? null;
  useEffect(() => {
    if (chosen && open?.sealed.includes(chosen)) {
      remember(null);
      setChosen(null);
    }
  }, [chosen, open]);
  const day = chosen ?? open?.day ?? FestivalClock.operatingDay(new Date());
  const choose = (d: string): void => {
    const mine = d === open?.day ? null : d;
    remember(mine);
    setChosen(mine);
  };
  return [day, choose, { open, chosen: chosen !== null && open !== null && chosen !== open.day }];
}

/**
 * **Which station this browser is** (James, Oct 4): picked once from the year's list on
 * Count-room settings, kept in this browser, and said in the day line — "Count station 1 ·
 * Counting SATURDAY". Never guessed: a laptop nobody has picked for is not a station yet.
 */
export const STATION_KEY = 'cr.station';
export function useStation(): {
  station: { id: string; name: string } | null;
  stations: { id: string; name: string }[];
  pick: (id: string) => void;
} {
  const load = useLoad<Settings>(`/money/countroom/${String(season())}/settings`);
  const stations = load.data && 'stations' in load.data ? load.data.stations : [];
  const [id, setId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(STATION_KEY);
    } catch {
      return null;
    }
  });
  const pick = (next: string): void => {
    try {
      localStorage.setItem(STATION_KEY, next);
    } catch {
      /* a private window keeps nothing */
    }
    setId(next);
  };
  return { station: stations.find((x) => x.id === id) ?? null, stations, pick };
}

// ---- the bridge on the station laptop (CR-12) ---------------------------------------------------

export interface BridgePass {
  id: string;
  bills: Bills;
  rejectCount: number;
  at: string;
  raw: string;
}
export interface BridgeStatus {
  ok: boolean;
  machine: 'up' | 'down' | 'unknown';
  lastLineAt: string | null;
  format: string | null;
  buffered: number;
  says: string;
}
export const BRIDGE_URL_KEY = 'cr.bridge';
export function bridgeUrl(): string {
  try {
    return localStorage.getItem(BRIDGE_URL_KEY) ?? 'http://127.0.0.1:7777';
  } catch {
    return 'http://127.0.0.1:7777';
  }
}
export async function bridgeStatus(): Promise<BridgeStatus | null> {
  try {
    const r = await fetch(`${bridgeUrl()}/status`, { signal: AbortSignal.timeout(1500) });
    if (!r.ok) return null;
    return (await r.json()) as BridgeStatus;
  } catch {
    return null;
  }
}
export async function bridgePasses(since: string | null): Promise<BridgePass[]> {
  try {
    const r = await fetch(
      `${bridgeUrl()}/passes${since ? `?since=${encodeURIComponent(since)}` : ''}`,
      { signal: AbortSignal.timeout(1500) },
    );
    if (!r.ok) return [];
    return ((await r.json()) as { passes: BridgePass[] }).passes;
  } catch {
    return [];
  }
}
export async function bridgeAck(id: string): Promise<void> {
  try {
    await fetch(`${bridgeUrl()}/passes/${encodeURIComponent(id)}/ack`, {
      method: 'POST',
      signal: AbortSignal.timeout(1500),
    });
  } catch {
    /* the bridge replays what was not acknowledged */
  }
}

/**
 * **A state in the room's words** (overnight 6, 2e): what a counter reads instead of the
 * machine's state names (`packages/domain/src/machines/count_room_v4.ts`). One map for the bag,
 * the seed bag, the count, the deposit and the day, so the same state reads the same everywhere.
 */
const STATE_WORDS: Record<string, string> = {
  available: 'on the shelf',
  in_transit: 'with the runner',
  received: 'waiting to be counted',
  counting: 'being counted',
  locked: 'counted and locked',
  exception: 'needs the lead',
  cleared: 'emptied, back on the shelf',
  retired: 'retired',
  ready: 'built, ready to hand out',
  in_field: 'in the field',
  open: 'open',
  verified: 'checked',
  reversed: 'reversed',
  building: 'being built',
  sealed: 'sealed',
  released: 'with the bank runner',
  deposited: 'at the bank',
  rehearsal_sealed: 'drill sealed',
};
export const stateSays = (state: string | null | undefined): string =>
  state ? (STATE_WORDS[state] ?? state.replace(/_/g, ' ')) : '—';
